import React from "react";
import FunnelTable from "./FunnelTable";
import FunnelChartCard from "./FunnelChartCard";
import { buildUnitEconomicsChart } from "./funnelChartUtils";
import {
  cleanLines,
  extractTitle,
  stripTitleLines,
  extractTableBlock,
  splitByHeading,
} from "./funnelUtils";
import styles from "./FunnelInsightCard.module.css";

/**
 * Parse the structured subsections that follow "What Does This Mean?".
 * Recognised labels: RESULT, HOW THIS NUMBER IS CALCULATED, MAIN DRIVER, SCALING IMPACT.
 * Returns { summary, result, calculation, mainDriver, scalingImpact }.
 */
const SECTION_LABELS = [
  { key: "result", pattern: /^RESULT:?$/i },
  { key: "calculation", pattern: /^HOW THIS NUMBER IS CALCULATED:?$/i },
  { key: "mainDriver", pattern: /^MAIN DRIVER:?$/i },
  { key: "scalingImpact", pattern: /^SCALING IMPACT:?$/i },
];

const parseStructuredSections = (lines = []) => {
  const sections = { summary: [], result: [], calculation: [], mainDriver: [], scalingImpact: [] };
  let current = "summary";

  for (const line of lines) {
    const match = SECTION_LABELS.find((s) => s.pattern.test(line.trim()));
    if (match) {
      current = match.key;
      continue;
    }
    sections[current].push(line);
  }

  return sections;
};

const SectionBlock = ({ label, lines }) => {
  if (!lines || !lines.length) return null;
  const text = lines.join(" ").trim();
  if (!text || text === "n/a") return null;

  return (
    <div className={styles.structuredBlock}>
      {label && <div className={styles.structuredLabel}>{label}</div>}
      <p className={styles.paragraph}>{text}</p>
    </div>
  );
};

const FunnelUnitEconomics = ({ markdown = "" }) => {
  const title = extractTitle(markdown) || "Unit Economics";
  const lines = stripTitleLines(cleanLines(markdown));
  const { table, after } = extractTableBlock(lines);
  const chart = buildUnitEconomicsChart(table);
  const { tail } = splitByHeading(after, "What Does This Mean?");
  const parsed = parseStructuredSections(tail);

  return (
    <div className={styles.card}>
      <h4 className={styles.title}>{title}</h4>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Unit Economics Chart</div>
        <FunnelChartCard
          title="Per Order Economics View"
          subtitle="This turns the big table into an at a glance money map."
          config={chart}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Unit Economics Table</div>
        {table && (
          <div className={styles.tableScrollWrap}>
            <FunnelTable table={table} />
          </div>
        )}
      </div>

      {/* Structured analysis sections */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>What Does This Mean?</div>
        <SectionBlock lines={parsed.summary} />
        <SectionBlock label="Result" lines={parsed.result} />
        <SectionBlock label="How This Number Is Calculated" lines={parsed.calculation} />
        <SectionBlock label="Main Driver" lines={parsed.mainDriver} />
        <SectionBlock label="Scaling Impact" lines={parsed.scalingImpact} />
      </div>
    </div>
  );
};

export default FunnelUnitEconomics;
